import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, FormHelperText, Typography } from '@mui/material';

const LeaveRequestForm = ({ open, onClose, onSubmit, initialData, title = 'Add Leave Request', loading, teachers = [], isTeacher = false, currentUserId = null, leaveBalance = null }) => {
  const [formData, setFormData] = useState({
    teacher_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    status: 'Pending',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        teacher_id: initialData.teacher_id || '',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        reason: initialData.reason || '',
        status: initialData.status || 'Pending',
      });
    } else {
      // For teachers creating new requests, auto-set their teacher_id
      const defaultTeacherId = isTeacher && currentUserId ? currentUserId : '';
      setFormData({ teacher_id: defaultTeacherId, start_date: '', end_date: '', reason: '', status: 'Pending' });
    }
    setErrors({});
  }, [initialData, isTeacher, currentUserId]);

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
    return diffDays;
  };

  const requestedDays = useMemo(() => calculateDays(formData.start_date, formData.end_date), [formData.start_date, formData.end_date]);
  const availableBalance = leaveBalance?.leave_balance ?? null;
  const exceedsBalance = isTeacher && availableBalance !== null && !initialData && requestedDays > availableBalance;
  const noBalanceRemaining = isTeacher && availableBalance !== null && !initialData && availableBalance <= 0;

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    
    // Real-time validation for date fields when both dates are filled
    if ((field === 'start_date' || field === 'end_date') && value) {
      const currentData = { ...formData, [field]: value };
      if (currentData.start_date && currentData.end_date) {
        const start = new Date(currentData.start_date);
        const end = new Date(currentData.end_date);
        if (end < start) {
          setErrors(prev => ({ ...prev, end_date: 'End date cannot be before start date' }));
        } else if (isTeacher && leaveBalance !== null && !initialData) {
          const requestedDays = calculateDays(currentData.start_date, currentData.end_date);
          const availableBalance = leaveBalance?.leave_balance ?? 0;
          if (requestedDays > availableBalance) {
            setErrors(prev => ({ ...prev, end_date: `Requested ${requestedDays} days exceeds available balance of ${availableBalance} days` }));
          } else {
            setErrors(prev => ({ ...prev, end_date: '' }));
          }
        }
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.teacher_id) newErrors.teacher_id = 'Teacher is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';
    
    // Check if end date is before start date
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        newErrors.end_date = 'End date cannot be before start date';
      }
    }

    // Check leave balance for teachers (only for new requests, not when editing)
    if (isTeacher && leaveBalance !== null && !initialData) {
      const requestedDays = calculateDays(formData.start_date, formData.end_date);
      const availableBalanceValue = availableBalance ?? 0;
      
      if (requestedDays > availableBalanceValue) {
        newErrors.end_date = `Requested ${requestedDays} day(s) exceeds available balance of ${availableBalanceValue} day(s)`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = { ...formData };
    if (initialData?.id) payload.id = initialData.id;
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          {/* Only show teacher dropdown for admins or when editing */}
          {(!isTeacher || initialData) && (
            <FormControl fullWidth margin="normal" error={!!errors.teacher_id}>
              <InputLabel>Teacher</InputLabel>
              <Select 
                value={formData.teacher_id} 
                onChange={handleChange('teacher_id')} 
                label="Teacher"
                disabled={isTeacher && !!initialData} // Disable for teachers editing their own requests
              >
                {teachers.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.full_name}</MenuItem>
                ))}
              </Select>
              {errors.teacher_id && <FormHelperText>{errors.teacher_id}</FormHelperText>}
            </FormControl>
          )}

          <TextField
            fullWidth
            margin="normal"
            type="date"
            label="Start Date"
            value={formData.start_date}
            onChange={handleChange('start_date')}
            InputLabelProps={{ shrink: true }}
            error={!!errors.start_date}
            helperText={errors.start_date}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            type="date"
            label="End Date"
            value={formData.end_date}
            onChange={handleChange('end_date')}
            InputLabelProps={{ shrink: true }}
            error={!!errors.end_date}
            helperText={errors.end_date}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Reason"
            value={formData.reason}
            onChange={handleChange('reason')}
            multiline
            rows={3}
          />

          {/* Only show status dropdown for admins */}
          {!isTeacher && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={handleChange('status')} label="Status">
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || exceedsBalance || noBalanceRemaining}>
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveRequestForm;


